import { useState, useEffect, useCallback } from "react";
import { Loader2, X, Plus, GripVertical, Star, Trash2, Upload, ImagePlus } from "lucide-react";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FORM_AMENITIES } from "@/lib/amenityIcons";
import { Checkbox } from "@/components/ui/checkbox";
import type { Tables } from "@/integrations/supabase/types";
import LocationPicker from "@/components/property/LocationPicker";
import { cn } from "@/lib/utils";

const PROVINCES = [
  "Bengo", "Benguela", "Bié", "Cabinda", "Cuando", "Cuando Cubango",
  "Cuanza Norte", "Cuanza Sul", "Cunene", "Huambo", "Huíla",
  "Icolo e Bengo", "Luanda", "Lunda Norte", "Lunda Sul", "Malanje",
  "Moxico", "Moxico Leste", "Namibe", "Uíge", "Zaire",
];

import { PROPERTY_TYPES as CANONICAL_TYPES } from "@/lib/propertyTypes";

import { tAuto } from "@/lib/tAuto";
const PROPERTY_TYPES = CANONICAL_TYPES.map((t) => ({ value: t.value, label: t.label }));


interface EditPropertyDialogProps {
  property: Tables<"properties"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

const EditPropertyDialog = ({ property, open, onOpenChange, onSaved }: EditPropertyDialogProps) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [bedrooms, setBedrooms] = useState(1);
  const [bathrooms, setBathrooms] = useState(1);
  const [maxGuests, setMaxGuests] = useState(2);
  const [pricePerNight, setPricePerNight] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [images, setImages] = useState<string[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [bookingType, setBookingType] = useState("confirmation");
  const [status, setStatus] = useState("active");
  const [hasInsurance, setHasInsurance] = useState(false);
  const [videoTourUrl, setVideoTourUrl] = useState("");
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);

  useEffect(() => {
    if (property && open) {
      setTitle(property.title || "");
      setDescription(property.description || "");
      setPropertyType(property.property_type || "");
      setProvince(property.province || "");
      setCity(property.city || "");
      setAddress(property.address || "");
      setBedrooms(property.bedrooms || 1);
      setBathrooms(property.bathrooms || 1);
      setMaxGuests(property.max_guests || 2);
      setPricePerNight(property.price_per_night || 0);
      setDiscountPercent((property as any).discount_percent || 0);
      setImages((property.images as string[]) || []);
      setSelectedAmenities((property.amenities as string[]) || []);
      setLatitude((property as any).latitude != null ? Number((property as any).latitude) : null);
      setLongitude((property as any).longitude != null ? Number((property as any).longitude) : null);
      setBookingType(property.booking_type || "confirmation");
      setStatus(property.status || "active");
      setHasInsurance(property.has_insurance || false);
      setVideoTourUrl((property as any).video_tour_url || "");
      
      const badgeActive = property.insurance_badge_active;
      const expiresAt = (property as any).insurance_badge_expires_at;
      const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false;
      setIsInsuranceActive(!!badgeActive && !isExpired);
      
      if (!!badgeActive && !isExpired) {
        setHasInsurance(true); // If active, checkbox should also be true
      }
    }
  }, [property, open]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !property) return;
    setUploading(true);
    const newUrls: string[] = [];
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop();
      const path = `${property.owner_id}/${property.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (!error) {
        const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
        newUrls.push(urlData.publicUrl);
      }
    }
    setImages(prev => [...prev, ...newUrls]);
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const setMainImage = (index: number) => {
    if (index === 0) return;
    setImages(prev => {
      const newArr = [...prev];
      const [item] = newArr.splice(index, 1);
      newArr.unshift(item);
      return newArr;
    });
  };

  const toggleAmenity = (amenity: string) => {
    setSelectedAmenities(prev =>
      prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]
    );
  };

  const handleSave = async () => {
    if (!property) return;
    if (title.length < 10) {
      toast({ title: tAuto("auto.components_property_editpropertydialog.1", "Título muito curto"), description: tAuto("auto.components_property_editpropertydialog.2", "Mínimo 10 caracteres."), variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("properties")
      .update({
        title,
        description,
        property_type: propertyType,
        province,
        city,
        address,
        latitude: latitude as any,
        longitude: longitude as any,
        bedrooms,
        bathrooms,
        max_guests: maxGuests,
        price_per_night: pricePerNight,
        discount_percent: discountPercent,
        booking_type: bookingType,
        status,
        has_insurance: hasInsurance,
        video_tour_url: videoTourUrl,
        images: images as any,
        amenities: selectedAmenities as any,
        updated_at: new Date().toISOString(),
      } as any)
      .eq("id", property.id);
    setSaving(false);
    if (error) {
      toast({ title: tAuto("auto.components_property_editpropertydialog.3", "Erro ao guardar"), description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: tAuto("auto.components_property_editpropertydialog.4", "Imóvel atualizado"), description: tAuto("auto.components_property_editpropertydialog.5", "As alterações foram guardadas com sucesso.") });
    onSaved();
    onOpenChange(false);
  };

  

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border/30 px-6 py-4">
          <DialogTitle className="text-lg font-bold">{tAuto("auto.components_property_editpropertydialog.6", "Editar Imóvel")}</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 space-y-6">
          {/* Images */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">Imagens</Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {images.map((img, idx) => (
                <div key={`${img}-${idx}`} className="relative group aspect-square rounded-xl overflow-hidden border border-border/40 bg-muted">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <div className="absolute top-1 left-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-primary/90 text-primary-foreground text-[9px] font-bold">
                      <Star className="w-2.5 h-2.5 fill-current" /> Principal
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                    {idx !== 0 && (
                      <button onClick={() => setMainImage(idx)} className="p-1.5 rounded-lg bg-white/90 hover:bg-white text-foreground" title="Definir como principal">
                        <Star className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => removeImage(idx)} className="p-1.5 rounded-lg bg-white/90 hover:bg-white text-destructive" title="Remover">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              <label className="aspect-square rounded-xl border-2 border-dashed border-border/50 hover:border-primary/40 bg-muted/30 hover:bg-muted/50 flex flex-col items-center justify-center cursor-pointer transition-all">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : (
                  <>
                    <ImagePlus className="w-5 h-5 text-muted-foreground mb-1" strokeWidth={1.5} />
                    <span className="text-[10px] text-muted-foreground font-medium">Adicionar</span>
                  </>
                )}
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} disabled={uploading} />
              </label>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid gap-4">
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.7", "Título")}</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Apartamento moderno em Talatona" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.8", "Descrição")}</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={tAuto("auto.components_property_editpropertydialog.9", "Descreva o imóvel...")} rows={4} className="resize-none" />
            </div>
          </div>

          {/* Type & Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.10", "Tipo de Imóvel")}</Label>
              <Select value={propertyType} onValueChange={setPropertyType}>
                <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.11", "Província")}</Label>
              <Select value={province} onValueChange={setProvince}>
                <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Cidade</Label>
              <Input value={city} onChange={e => setCity(e.target.value)} placeholder="Luanda, Talatona..." />
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.12", "Endereço")}</Label>
              <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Rua, nº..." />
            </div>
          </div>

          {/* Map picker */}
          <div>
            <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
              Localização no mapa {latitude == null && <span className="text-destructive">(em falta — marque o pin)</span>}
            </Label>
            <LocationPicker
              initialLat={latitude}
              initialLng={longitude}
              province={province}
              city={city}
              address={address}
              onChange={(v) => { setLatitude(v.latitude); setLongitude(v.longitude); }}
              height="240px"
            />
          </div>

          {/* Numbers */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Quartos</Label>
              <Input type="number" min={1} value={bedrooms} onChange={e => setBedrooms(Number(e.target.value))} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.13", "Casas de banho")}</Label>
              <Input type="number" min={1} value={bathrooms} onChange={e => setBathrooms(Number(e.target.value))} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.14", "Máx. hóspedes")}</Label>
              <Input type="number" min={1} value={maxGuests} onChange={e => setMaxGuests(Number(e.target.value))} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.15", "Preço/noite (Kz)")}</Label>
              <Input type="number" min={1000} value={pricePerNight} onChange={e => setPricePerNight(Number(e.target.value))} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Desconto (%)</Label>
              <Select value={discountPercent.toString()} onValueChange={(val) => setDiscountPercent(Number(val))}>
                <SelectTrigger><SelectValue placeholder="Sem desconto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Sem desconto</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="15">15%</SelectItem>
                  <SelectItem value="20">20%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Opções Comerciais */}
          <div className="p-4 rounded-xl border border-border/40 bg-muted/20 space-y-4">
            <h4 className="text-sm font-bold flex items-center gap-2">
              Opções Comerciais
              {discountPercent > 0 && (
                <span className="text-xs font-medium bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded-full ml-auto">
                  Preço Final: {new Intl.NumberFormat("pt-AO", { style: "currency", currency: "AOA", maximumFractionDigits: 0 }).format(pricePerNight * (1 - discountPercent / 100))}
                </span>
              )}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.16", "Tipo de Reserva")}</Label>
                <Select value={bookingType} onValueChange={setBookingType}>
                  <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmation">{tAuto("auto.components_property_editpropertydialog.17", "Confirmação Manual")}</SelectItem>
                    <SelectItem value="instant">{tAuto("auto.components_property_editpropertydialog.18", "Reserva Instantânea")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.19", "Estado do Imóvel")}</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="inactive">Inativo (Oculto)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{tAuto("auto.components_property_editpropertydialog.20", "Link de Vídeo Tour (Opcional)")}</Label>
                <Input value={videoTourUrl} onChange={e => setVideoTourUrl(e.target.value)} placeholder={tAuto("auto.components_property_editpropertydialog.21", "https://youtube.com/...")} />
              </div>
              <div className="sm:col-span-2 flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-background">
                <Checkbox 
                  id="hasInsurance" 
                  checked={hasInsurance} 
                  onCheckedChange={(checked) => setHasInsurance(checked as boolean)} 
                  disabled={isInsuranceActive}
                  className="mt-0.5 border-gray-300 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                />
                <Label htmlFor="hasInsurance" className={cn("text-sm font-medium cursor-pointer flex-1", isInsuranceActive && "opacity-70")}>
                  Activar Protecção BeraStays
                  <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                    {isInsuranceActive 
                      ? "O imóvel já possui um seguro ativo (aprovado pela seguradora ou administração)."
                      : "Participar no programa de seguro contra danos e acidentes."}
                  </span>
                </Label>
                {isInsuranceActive && (
                  <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Ativo</span>
                )}
              </div>
            </div>
          </div>

          {/* Amenities */}
          {FORM_AMENITIES.length > 0 && (
            <div>
              <Label className="text-xs font-semibold text-muted-foreground mb-2 block">Comodidades</Label>
              <div className="flex flex-wrap gap-2">
                {FORM_AMENITIES.map(amenity => (
                  <button
                    key={amenity.id}
                    type="button"
                    onClick={() => toggleAmenity(amenity.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      selectedAmenities.includes(amenity.id)
                        ? "bg-primary/10 border-primary/30 text-primary"
                        : "bg-muted/30 border-border/40 text-muted-foreground hover:border-border"
                    }`}
                  >
                    {amenity.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border/30 px-6 py-4 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>{tAuto("auto.components_property_editpropertydialog.22", "Cancelar")}</Button>
          <Button onClick={handleSave} disabled={saving} className="min-w-[120px]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Guardar Alterações
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditPropertyDialog;
